import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { oneKlicks } from '../../../SDK/oneclick';
import { getuserdata } from '../../../services/apiService';
import { toast } from 'react-toastify';
import ImageShow from '../../../Uitiles/ImageShow';

const AddEmployeeModal = ({ show, onHide, onSuccess, editingEmployee = null }) => {
    const [formData, setFormData] = useState({
        staff_id: '',
        employee_id: '',
        base_salary: '',
        allowances: '',
        deductions: '',
        net_salary: '',
        joining_date: '',
        department: '',
        designation: '',
        work_location: '',
        reporting_manager: '',
        employment_type: 'full_time',
        work_shift: 'day',
        probation_period: '',
        contract_end_date: '',
        emergency_contact: '',
        emergency_phone: '',
        bank_account_number: '',
        bank_name: '',
        ifsc_code: '',
        pan_number: '',
        aadhar_number: '',
        status: 'active',
        notes: ''
    });

    const [staffList, setStaffList] = useState([]);
    const [selectedStaff, setSelectedStaff] = useState(null);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    const employmentTypes = [
        { value: 'full_time', label: 'Full Time' },
        { value: 'part_time', label: 'Part Time' },
        { value: 'contract', label: 'Contract' },
        { value: 'intern', label: 'Intern' },
        { value: 'consultant', label: 'Consultant' }
    ];

    const workShifts = [
        { value: 'day', label: 'Day Shift (9 AM - 6 PM)' },
        { value: 'night', label: 'Night Shift (9 PM - 6 AM)' },
        { value: 'rotating', label: 'Rotating Shift' },
        { value: 'flexible', label: 'Flexible Hours' }
    ];

    const departments = [
        'Administration',
        'Human Resources',
        'Finance',
        'IT/Technology',
        'Operations',
        'Marketing',
        'Sales',
        'Customer Service',
        'Healthcare',
        'Security',
        'Maintenance',
        'Other'
    ];

    const designations = [
        'CEO',
        'Manager',
        'Supervisor',
        'Senior Executive',
        'Executive',
        'Assistant',
        'Coordinator',
        'Specialist',
        'Analyst',
        'Representative',
        'Technician',
        'Other'
    ];

    useEffect(() => {
        if (show) {
            fetchStaffList();
            if (editingEmployee) {
                setFormData({
                    staff_id: editingEmployee.staff_id || '',
                    employee_id: editingEmployee.employee_id || '',
                    base_salary: editingEmployee.base_salary || '',
                    allowances: editingEmployee.allowances || '',
                    deductions: editingEmployee.deductions || '',
                    net_salary: editingEmployee.net_salary || '',
                    joining_date: editingEmployee.joining_date || '',
                    department: editingEmployee.department || '',
                    designation: editingEmployee.designation || '',
                    work_location: editingEmployee.work_location || '',
                    reporting_manager: editingEmployee.reporting_manager || '',
                    employment_type: editingEmployee.employment_type || 'full_time',
                    work_shift: editingEmployee.work_shift || 'day',
                    probation_period: editingEmployee.probation_period || '',
                    contract_end_date: editingEmployee.contract_end_date || '',
                    emergency_contact: editingEmployee.emergency_contact || '',
                    emergency_phone: editingEmployee.emergency_phone || '',
                    bank_account_number: editingEmployee.bank_account_number || '',
                    bank_name: editingEmployee.bank_name || '',
                    ifsc_code: editingEmployee.ifsc_code || '',
                    pan_number: editingEmployee.pan_number || '',
                    aadhar_number: editingEmployee.aadhar_number || '',
                    status: editingEmployee.status || 'active',
                    notes: editingEmployee.notes || ''
                });
                if (editingEmployee.staff_id) {
                    setSelectedStaff(staffList.find(staff => staff.id === editingEmployee.staff_id));
                }
            } else {
                resetForm();
            }
        }
    }, [show, editingEmployee]);

    useEffect(() => {
        if (formData.staff_id) {
            const staff = staffList.find(s => s.id === parseInt(formData.staff_id));
            if (staff) {
                setSelectedStaff(staff);
                // Auto-generate employee ID
                if (!editingEmployee && !formData.employee_id) {
                    generateEmployeeId(staff);
                }
                // Auto-calculate base salary based on role
                if (!editingEmployee && !formData.base_salary) {
                    autoCalculateSalary(staff);
                }
            }
        }
    }, [formData.staff_id, staffList]);

    useEffect(() => {
        // Auto-calculate net salary when base salary, allowances, or deductions change
        const baseSalary = parseFloat(formData.base_salary) || 0;
        const allowances = parseFloat(formData.allowances) || 0;
        const deductions = parseFloat(formData.deductions) || 0;
        const netSalary = baseSalary + allowances - deductions;
        
        if (baseSalary > 0 || allowances > 0 || deductions > 0) {
            setFormData(prev => ({
                ...prev,
                net_salary: netSalary.toString()
            }));
        }
    }, [formData.base_salary, formData.allowances, formData.deductions]);

    const fetchStaffList = async () => {
        try {
            const tableSchema = '';
            const tableName = 'users';
            const conditions = {
                user_type: 'staff'
            };
            const orderBy = 'id DESC';
            const search = '';
            const pagination = {};
            const margedata = [
                {
                    target_table: 'roles',
                    target_column: 'id',
                    target_value: 'role',
                    target_label: 'roles'
                }
            ];

            const response = await oneKlicks.read(tableSchema, tableName, conditions, orderBy, search, pagination, margedata);
            
            if (response.success) {
                setStaffList(response.data);
            } else {
                toast.error('Failed to fetch staff list');
            }
        } catch (error) {
            console.error('Error fetching staff list:', error);
            toast.error('Error fetching staff list');
        }
    };

    const generateEmployeeId = (staff) => {
        const currentYear = new Date().getFullYear();
        const randomNum = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
        const employeeId = `EMP${currentYear}${randomNum}`;
        
        setFormData(prev => ({
            ...prev,
            employee_id: employeeId
        }));
    };

    const autoCalculateSalary = (staff) => {
        const role = staff.roles?.[0];
        if (role) {
            let baseSalary = 0;
            
            const salaryRanges = {
                'admin': 80000,
                'manager': 60000,
                'supervisor': 45000,
                'nurse': 35000,
                'receptionist': 25000,
                'cleaner': 15000,
                'security': 20000,
                'other': 25000
            };

            const roleName = role.role_name?.toLowerCase();
            baseSalary = salaryRanges[roleName] || salaryRanges['other'];

            setFormData(prev => ({
                ...prev,
                base_salary: baseSalary.toString()
            }));
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.staff_id) {
            newErrors.staff_id = 'Please select a staff member';
        }
        if (!formData.employee_id) {
            newErrors.employee_id = 'Please enter employee ID';
        }
        if (!formData.base_salary || parseFloat(formData.base_salary) <= 0) {
            newErrors.base_salary = 'Please enter a valid base salary';
        }
        if (!formData.joining_date) {
            newErrors.joining_date = 'Please select joining date';
        }
        if (!formData.department) {
            newErrors.department = 'Please select department';
        }
        if (!formData.designation) {
            newErrors.designation = 'Please select designation';
        }
        if (formData.employment_type === 'contract' && !formData.contract_end_date) {
            newErrors.contract_end_date = 'Contract end date is required for contract employees';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            toast.error('Please fix the errors before submitting');
            return;
        }

        setLoading(true);
        try {
            const userId = await getuserdata('id');
            
            const employeeData = {
                staff_id: parseInt(formData.staff_id),
                employee_id: formData.employee_id,
                base_salary: parseFloat(formData.base_salary),
                allowances: parseFloat(formData.allowances) || 0,
                deductions: parseFloat(formData.deductions) || 0,
                net_salary: parseFloat(formData.net_salary) || 0,
                joining_date: formData.joining_date,
                department: formData.department,
                designation: formData.designation,
                work_location: formData.work_location || null,
                reporting_manager: formData.reporting_manager || null,
                employment_type: formData.employment_type,
                work_shift: formData.work_shift,
                probation_period: formData.probation_period || null,
                contract_end_date: formData.contract_end_date || null,
                emergency_contact: formData.emergency_contact || null,
                emergency_phone: formData.emergency_phone || null,
                bank_account_number: formData.bank_account_number || null,
                bank_name: formData.bank_name || null,
                ifsc_code: formData.ifsc_code || null,
                pan_number: formData.pan_number || null,
                aadhar_number: formData.aadhar_number || null,
                status: formData.status,
                notes: formData.notes || null,
                created_by: userId
            };

            const tableSchema = '';
            const tableName = 'employees';
            
            let response;
            if (editingEmployee) {
                // Update existing employee
                const conditions = { id: editingEmployee.id };
                response = await oneKlicks.update(tableSchema, tableName, employeeData, conditions);
            } else {
                // Create new employee
                const conditions = {};
                response = await oneKlicks.create(tableSchema, tableName, employeeData, conditions);
            }

            if (response.success === true) {
                toast.success(editingEmployee ? 'Employee updated successfully' : 'Employee added successfully');
                onSuccess && onSuccess(response.data);
                onHide();
            } else {
                toast.error(response.message || 'Failed to save employee');
            }
        } catch (error) {
            console.error('Error saving employee:', error);
            toast.error('Error saving employee');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            staff_id: '',
            employee_id: '',
            base_salary: '',
            allowances: '',
            deductions: '',
            net_salary: '',
            joining_date: '',
            department: '',
            designation: '',
            work_location: '',
            reporting_manager: '',
            employment_type: 'full_time',
            work_shift: 'day',
            probation_period: '',
            contract_end_date: '',
            emergency_contact: '',
            emergency_phone: '',
            bank_account_number: '',
            bank_name: '',
            ifsc_code: '',
            pan_number: '',
            aadhar_number: '',
            status: 'active',
            notes: ''
        });
        setSelectedStaff(null);
        setErrors({});
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" centered scrollable>
            <Modal.Header closeButton>
                <Modal.Title>
                    {editingEmployee ? 'Edit Employee' : 'Add New Employee'}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                <Form onSubmit={handleSubmit}>
                    {/* Basic Information */}
                    <div className="card mb-3">
                        <div className="card-header">
                            <h6 className="mb-0">Basic Information</h6>
                        </div>
                        <div className="card-body">
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Select Staff Member *</Form.Label>
                                        <Form.Select
                                            name="staff_id"
                                            className="form-control"
                                            value={formData.staff_id}
                                            onChange={handleInputChange}
                                            isInvalid={!!errors.staff_id}
                                        >
                                            <option value="">Choose staff member...</option>
                                            {staffList.map(staff => (
                                                <option key={staff.id} value={staff.id} style={{backgroundColor: staff.status === 0 ? '#f8d7da' : '#d4edda'}} disabled={staff.status === 0}>
                                                    {staff.first_name} {staff.last_name} 
                                                    {staff.roles?.[0]?.role_name && ` (${staff.roles[0].role_name})`}
                                                </option>
                                            ))}
                                        </Form.Select>
                                        {errors.staff_id && (
                                            <Form.Control.Feedback type="invalid">
                                                {errors.staff_id}
                                            </Form.Control.Feedback>
                                        )}
                                        {selectedStaff && (
                                            <div className="d-flex align-items-center mt-2" >
                                                <ImageShow 
                                                    url={selectedStaff.profile_photo_url} 
                                                    width={50} 
                                                    height={40}
                                                    style={{ borderRadius: '50%', marginRight: '10px' }}
                                                />
                                                <div>
                                                    <div className="fw-bold">
                                                        {selectedStaff.first_name} {selectedStaff.last_name}
                                                    </div>
                                                    <small className="text-muted">
                                                        {selectedStaff.roles?.[0]?.role_name || 'No Role'}
                                                    </small>
                                                </div>
                                            </div>
                                        )}
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Employee ID *</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="employee_id"
                                            className="form-control"
                                            value={formData.employee_id}
                                            onChange={handleInputChange}
                                            placeholder="Auto-generated or enter manually"
                                            isInvalid={!!errors.employee_id}
                                        />
                                        {errors.employee_id && (
                                            <Form.Control.Feedback type="invalid">
                                                {errors.employee_id}
                                            </Form.Control.Feedback>
                                        )}
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Joining Date *</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="joining_date"
                                            className="form-control"
                                            value={formData.joining_date}
                                            onChange={handleInputChange}
                                            isInvalid={!!errors.joining_date}
                                        />
                                        {errors.joining_date && (
                                            <Form.Control.Feedback type="invalid">
                                                {errors.joining_date}
                                            </Form.Control.Feedback>
                                        )}
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Employment Type *</Form.Label>
                                        <Form.Select
                                            name="employment_type"
                                            className="form-control"
                                            value={formData.employment_type}
                                            onChange={handleInputChange}
                                        >
                                            {employmentTypes.map(type => (
                                                <option key={type.value} value={type.value}>
                                                    {type.label}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Status</Form.Label>
                                        <Form.Select
                                            name="status"
                                            className="form-control"
                                            value={formData.status}
                                            onChange={handleInputChange}
                                        >
                                            <option value="active">Active</option>
                                            <option value="inactive">Inactive</option>
                                            <option value="on_leave">On Leave</option>
                                            <option value="terminated">Terminated</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>
                        </div>
                    </div>

                    {/* Job Information */}
                    <div className="card mb-3">
                        <div className="card-header">
                            <h6 className="mb-0">Job Information</h6>
                        </div>
                        <div className="card-body">
                            <Row>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Department *</Form.Label>
                                        <Form.Select
                                            name="department"
                                            className="form-control"
                                            value={formData.department}
                                            onChange={handleInputChange}
                                            isInvalid={!!errors.department}
                                        >
                                            <option value="">Select department...</option>
                                            {departments.map(dept => (
                                                <option key={dept} value={dept}>
                                                    {dept}
                                                </option>
                                            ))}
                                        </Form.Select>
                                        {errors.department && (
                                            <Form.Control.Feedback type="invalid">
                                                {errors.department}
                                            </Form.Control.Feedback>
                                        )}
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Designation *</Form.Label>
                                        <Form.Select
                                            name="designation"
                                            className="form-control"
                                            value={formData.designation}
                                            onChange={handleInputChange}
                                            isInvalid={!!errors.designation}
                                        >
                                            <option value="">Select designation...</option>
                                            {designations.map(desg => (
                                                <option key={desg} value={desg}>
                                                    {desg}
                                                </option>
                                            ))}
                                        </Form.Select>
                                        {errors.designation && (
                                            <Form.Control.Feedback type="invalid">
                                                {errors.designation}
                                            </Form.Control.Feedback>
                                        )}
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Work Shift</Form.Label>
                                        <Form.Select
                                            name="work_shift"
                                            className="form-control"
                                            value={formData.work_shift}
                                            onChange={handleInputChange}
                                        >
                                            {workShifts.map(shift => (
                                                <option key={shift.value} value={shift.value}>
                                                    {shift.label}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Work Location</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="work_location"
                                            className="form-control"
                                            value={formData.work_location}
                                            onChange={handleInputChange}
                                            placeholder="Enter work location"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Reporting Manager</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="reporting_manager"
                                            className="form-control"
                                            value={formData.reporting_manager}
                                            onChange={handleInputChange}
                                            placeholder="Enter reporting manager name"
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Probation Period (months)</Form.Label>
                                        <Form.Control
                                            type="number"
                                            name="probation_period"
                                            className="form-control"
                                            value={formData.probation_period}
                                            onChange={handleInputChange}
                                            placeholder="Enter probation period in months"
                                            min="0"
                                            max="12"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    {formData.employment_type === 'contract' && (
                                        <Form.Group className="mb-3">
                                            <Form.Label>Contract End Date *</Form.Label>
                                            <Form.Control
                                                type="date"
                                                name="contract_end_date"
                                                className="form-control"
                                                value={formData.contract_end_date}
                                                onChange={handleInputChange}
                                                isInvalid={!!errors.contract_end_date}
                                            />
                                            {errors.contract_end_date && (
                                                <Form.Control.Feedback type="invalid">
                                                    {errors.contract_end_date}
                                                </Form.Control.Feedback>
                                            )}
                                        </Form.Group>
                                    )}
                                </Col>
                            </Row>
                        </div>
                    </div>

                    {/* Salary Information */}
                    <div className="card mb-3">
                        <div className="card-header">
                            <h6 className="mb-0">Salary Information</h6>
                        </div>
                        <div className="card-body">
                            <Row>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Base Salary *</Form.Label>
                                        <Form.Control
                                            type="number"
                                            name="base_salary"
                                            className="form-control"
                                            value={formData.base_salary}
                                            onChange={handleInputChange}
                                            placeholder="Enter base salary"
                                            isInvalid={!!errors.base_salary}
                                            min="0"
                                            step="0.01"
                                        />
                                        {errors.base_salary && (
                                            <Form.Control.Feedback type="invalid">
                                                {errors.base_salary}
                                            </Form.Control.Feedback>
                                        )}
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Allowances</Form.Label>
                                        <Form.Control
                                            type="number"
                                            name="allowances"
                                            className="form-control"
                                            value={formData.allowances}
                                            onChange={handleInputChange}
                                            placeholder="Enter allowances"
                                            min="0"
                                            step="0.01"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Deductions</Form.Label>
                                        <Form.Control
                                            type="number"
                                            name="deductions"
                                            className="form-control"
                                            value={formData.deductions}
                                            onChange={handleInputChange}
                                            placeholder="Enter deductions"
                                            min="0"
                                            step="0.01"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Net Salary</Form.Label>
                                        <Form.Control
                                            type="number"
                                            name="net_salary"
                                            className="form-control"
                                            value={formData.net_salary}
                                            readOnly
                                            style={{ backgroundColor: '#f8f9fa' }}
                                        />
                                        <Form.Text className="text-muted">
                                            Auto-calculated
                                        </Form.Text>
                                    </Form.Group>
                                </Col>
                            </Row>
                        </div>
                    </div>

                    {/* Contact Information */}
                    <div className="card mb-3">
                        <div className="card-header">
                            <h6 className="mb-0">Contact Information</h6>
                        </div>
                        <div className="card-body">
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Emergency Contact</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="emergency_contact"
                                            className="form-control"
                                            value={formData.emergency_contact}
                                            onChange={handleInputChange}
                                            placeholder="Enter emergency contact name"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Emergency Phone</Form.Label>
                                        <Form.Control
                                            type="tel"
                                            name="emergency_phone"
                                            className="form-control"
                                            value={formData.emergency_phone}
                                            onChange={handleInputChange}
                                            placeholder="Enter emergency phone number"
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </div>
                    </div>

                    {/* Bank Information */}
                    <div className="card mb-3">
                        <div className="card-header">
                            <h6 className="mb-0">Bank Information</h6>
                        </div>
                        <div className="card-body">
                            <Row>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Bank Account Number</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="bank_account_number"
                                            className="form-control"
                                            value={formData.bank_account_number}
                                            onChange={handleInputChange}
                                            placeholder="Enter bank account number"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Bank Name</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="bank_name"
                                            className="form-control"
                                            value={formData.bank_name}
                                            onChange={handleInputChange}
                                            placeholder="Enter bank name"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>IFSC Code</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="ifsc_code"
                                            className="form-control"
                                            value={formData.ifsc_code}
                                            onChange={handleInputChange}
                                            placeholder="Enter IFSC code"
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </div>
                    </div>

                    {/* Documents */}
                    <div className="card mb-3">
                        <div className="card-header">
                            <h6 className="mb-0">Documents</h6>
                        </div>
                        <div className="card-body">
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>PAN Number</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="pan_number"
                                            className="form-control"
                                            value={formData.pan_number}
                                            onChange={handleInputChange}
                                            placeholder="Enter PAN number"
                                            maxLength="10"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Aadhar Number</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="aadhar_number"
                                            className="form-control"
                                            value={formData.aadhar_number}
                                            onChange={handleInputChange}
                                            placeholder="Enter Aadhar number"
                                            maxLength="12"
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </div>
                    </div>

                    {/* Notes */}
                    <div className="card mb-3">
                        <div className="card-header">
                            <h6 className="mb-0">Additional Notes</h6>
                        </div>
                        <div className="card-body">
                            <Form.Group className="mb-3">
                                <Form.Label>Notes</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    name="notes"
                                    className="form-control"
                                    value={formData.notes}
                                    onChange={handleInputChange}
                                    placeholder="Add any additional notes about this employee..."
                                />
                            </Form.Group>
                        </div>
                    </div>

                    {Object.keys(errors).length > 0 && (
                        <Alert variant="danger">
                            <strong>Please fix the following errors:</strong>
                            <ul className="mb-0 mt-2">
                                {Object.entries(errors).map(([field, error]) => (
                                    <li key={field}>{error}</li>
                                ))}
                            </ul>
                        </Alert>
                    )}
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide} disabled={loading}>
                    Cancel
                </Button>
                <Button 
                    variant="primary" 
                    onClick={handleSubmit}
                    disabled={loading}
                >
                    {loading ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            {editingEmployee ? 'Updating...' : 'Adding...'}
                        </>
                    ) : (
                        editingEmployee ? 'Update Employee' : 'Add Employee'
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default AddEmployeeModal;
